import React, { useEffect, useState } from 'react';
import FolderView, {
  FOLDER_SEPARATOR,
} from '@components/Core/Folders/FolderView/FolderView.component';
import { useTranslation } from 'react-i18next';
import { FileItemProps } from '@components/Core/Folders/FileItem/FileItem.component';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TemplateButton from './components/TemplateButton/TemplateButton.component';
import { Icons } from '@Assets/SvgIcons';
import { MergefloServices } from '@Helper/MergefloServices';
import FileSystemNavigator from '@components/Core/Folders/FileSystemNavigator/FileSystemNavigator.component';
import NewFolderModal from './components/NewFolderModal/NewFolderModal.component';
import RemoveFileDialog from './components/RemoveFileDialog/RemoveFileDialog.component';
import {
  addFileToCurrentPath,
  decodeFileName,
  encodeFileName,
  FileTransaction,
  findFileById,
  moveFileToTargetPath,
  removeFileFromCurrentPath,
  replaceFileFromCurrentPath,
} from '@Helper/FolderHelper';
import Header from '@components/Layout/Header/Header';
import MoveFileDialog from './components/MoveFileDialog/MoveFileDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { RouteEnums } from '@Helper/Enums';
import { unsecuredCopyToClipboard } from '@Helper/utils';
import UndoToast from '@components/Popups/UndoToast/UndoToast.component';
import { toast, ToastContainer } from 'react-toastify';
import { useAppDispatch } from '@Store/hooks';
import { postProjectTemplate } from '@Store/feature/personalTemplatesDetailSlice';
import './PersonalTemplates.scss';
import ImportCSVTemplate from './components/ImportCSVTemplate/ImportCSVTemplate';

export type PersonalTemplatesProps = {};

export type CreateType = 'template' | 'folder';

const PersonalTemplates = ({}: PersonalTemplatesProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const rootPathName = t('templates.personal_templates');
  const mergefloService = new MergefloServices();
  const [files, setFiles] = useState<FileItemProps[]>([]);
  const [isNewModalOpen, setNewModalOpen] = useState<boolean>(false);
  const [openRemoveFileDialog, removeFileDialog] = useState<boolean>(false);
  const [moveFileDialog, setMoveFileDialog] = useState<boolean>(false);
  const [targetFile, setTargetFile] = useState<FileItemProps | null>(null);
  const query = new URLSearchParams(useLocation().search);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [targetPath, setTargetPath] = useState<string>(
    decodeFileName(query.get('path') ?? rootPathName)
  );
  const [openImportTemplate, setOpenImportTemplate] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    mergefloService.getPersonalTemplates().then((files: FileItemProps[]) => {
      setFiles([...files]);
      dispatch(
        postProjectTemplate(
          JSON.parse(JSON.stringify(files)) as FileItemProps[]
        )
      );
      if (query.has('path')) {
        setCurrentPath(
          decodeFileName(query.get('path') ?? '').split(FOLDER_SEPARATOR)
        );
      }
    });
  }, []);

  const createNewFolder = () => {
    setNewModalOpen(true);
  };

  const handleCreateNewFolder = (file: FileItemProps) => {
    setNewModalOpen(false);
    const functionName = targetFile
      ? replaceFileFromCurrentPath
      : addFileToCurrentPath;
    const newFiles = functionName(
      {
        name: rootPathName,
        type: 'folder',
        files,
      } as FileItemProps,
      file,
      targetFile ? getFilePaths(targetFile) : currentPath,
      targetFile ?? ({} as FileItemProps)
    );
    setFiles([...newFiles.files]);
    mergefloService
      .setTemplateStructure({ personal_templates: newFiles.files })
      .then(_ => {
        setTargetFile(null);
      });
  };

  const onActionMenuItemClick = (file: FileItemProps, action: string) => {
    const fileWithPath = findFileById(file, files);
    setTargetFile(fileWithPath);
    const actions = {
      delete: () => {
        removeFileDialog(true);
      },
      edit: () => {
        setNewModalOpen(true);
      },
      move: () => {
        setMoveFileDialog(true);
      },
      rename: () => {
        setNewModalOpen(true);
      },
      new_tab: () => {
        const pathParam = encodeFileName(
          [...getFilePaths(fileWithPath), file.name].join(FOLDER_SEPARATOR)
        );
        window.open(`${RouteEnums.PersonalTemplates}?path=${pathParam}`);
      },
      copy: () => {
        const pathParam = encodeFileName(
          [...getFilePaths(fileWithPath), file.name].join(FOLDER_SEPARATOR)
        );
        unsecuredCopyToClipboard(
          `${window.location.origin}${RouteEnums.PersonalTemplates}?path=${pathParam}`
        );
      },
    };
    actions[action as keyof typeof actions]();
  };

  const handleRemoveFile = (file: FileItemProps) => {
    removeFileDialog(false);
    const fileWithPath = findFileById(file, files);
    const newFiles = removeFileFromCurrentPath(
      {
        name: rootPathName,
        files,
      } as FileItemProps,
      fileWithPath,
      getFilePaths(fileWithPath)
    );
    setFiles([...newFiles.files]);
    setTargetFile(null);
    mergefloService.setTemplateStructure({
      personal_templates: newFiles.files,
    });
  };

  const handlePathChange = (paths: string[]) => {
    setCurrentPath(paths);
  };

  const onPathSelected = (paths: string[]) => {
    setTargetPath(
      `${encodeFileName([rootPathName, ...paths].join(FOLDER_SEPARATOR))}`
    );
  };

  const getFilePaths = (file: FileItemProps): string[] => {
    const { path = '' } = file;
    return path.split(FOLDER_SEPARATOR).filter(item => item.length > 0);
  };

  const runFileTransaction = (paths: string[], targetPaths: string[]) => {
    if (targetFile) {
      const transaction: FileTransaction = {
        sourcePath: {
          name: rootPathName,
          files,
        } as FileItemProps,
        file: targetFile,
        paths: getFilePaths(targetFile),
        targetPaths,
      };
      const newFiles = moveFileToTargetPath(transaction);
      setFiles([...newFiles.files]);
      mergefloService.setTemplateStructure({
        personal_templates: newFiles.files,
      });
    }
  };

  const onMoveFileToTargetPath = (targetPaths: string[]) => {
    if (targetFile) {
      setMoveFileDialog(false);
      runFileTransaction([rootPathName, ...currentPath], targetPaths);
      toast(
        <UndoToast
          text={`${targetFile.name} move to ${targetPaths.reverse()[0]}`}
          onUndo={() => {
            runFileTransaction(targetPaths, [rootPathName, ...currentPath]);
          }}
        />,
        {
          onClose: () => {
            setTargetFile(null);
          },
        }
      );
    }
  };

  const handleDialogClose = () => {
    setMoveFileDialog(false);
    removeFileDialog(false);
    setNewModalOpen(false);
    setTargetFile(null);
  };

  const handleCreateNewTemplate = async () => {
    await mergefloService.createNewTemplateStructure().then(res => {
      addTemplateStructure({
        ...res,
        id: res.templateId,
        name: res.templateName,
        description: res.templateDesc,
        copiedTo: res.copiedTo,
        contains: res.contains,
        htmlName: res.templateName,
      });
    });
  };

  const addTemplateStructure = async (file: FileItemProps) => {
    const newFiles = addFileToCurrentPath(
      {
        name: rootPathName,
        files,
      } as FileItemProps,
      file,
      currentPath
    );
    setFiles([...newFiles.files]);

    await mergefloService
      .setTemplateStructure({ personal_templates: newFiles.files })
      .then(_ => {
        setTargetFile(null);
        console.log('file', file);

        navigate(RouteEnums.PersonalTemplatesDetail + file.templateId);
      });
  };

  return (
    <div className="personal-templates">
      <div className="personal-templates__side-menu">
        <div className="personal-templates__side-menu__logo">
          <Icons.BookshelfLogo />
        </div>
        <div className="personal-templates__side-menu__navigation">
          <h6>{t('commons.templates')} </h6>
          <FileSystemNavigator
            currentPath={currentPath}
            rootPathName={rootPathName}
            files={files}
            onPathSelected={onPathSelected}
          />
        </div>
      </div>
      <div className="personal-templates__content">
        <Header />
        <div className="personal-templates__content__body">
          <div className="personal-templates__content__header">
            <TemplateButton
              theme="purple"
              title={t('templates.create_new_template')}
              text={t('templates.workplan_tasks_milestones')}
              iconLeft={<Icons.Workplan width="28" height="28" color="#FFF" />}
              iconRight={<Icons.Add width="28" height="28" color="#FFF" />}
              handleClick={handleCreateNewTemplate}
            />
            <TemplateButton
              title={t('templates.import_from_csv')}
              text={t('templates.use_your_previous_work')}
              iconLeft={
                <Icons.CloudUpload width="28" height="28" color="#6E6B8C" />
              }
              iconRight={<Icons.Add width="28" height="28" color="#6E6B8C" />}
              handleClick={() => setOpenImportTemplate(true)}
            />
            <TemplateButton
              title={t('templates.add_new_folder')}
              text={t('templates.organize_your_templates')}
              iconLeft={
                <FolderOutlinedIcon
                  style={{ fontSize: '27.604px', color: '#9997AF' }}
                />
              }
              iconRight={<Icons.Add width="28" height="28" color="#6E6B8C" />}
              handleClick={createNewFolder}
            />
          </div>
          {files.length > 0 && (
            <FolderView
              files={files}
              homePath={rootPathName}
              onActionMenuItemClick={onActionMenuItemClick}
              handlePathChange={handlePathChange}
              targetPath={targetPath}
              listType="list"
            />
          )}
        </div>
        <NewFolderModal
          open={isNewModalOpen}
          file={targetFile || undefined}
          handleClose={handleDialogClose}
          handleSubmit={handleCreateNewFolder}
        />
        <MoveFileDialog
          open={moveFileDialog}
          files={files}
          handleClose={handleDialogClose}
          handleSubmit={onMoveFileToTargetPath}
        />
        <ImportCSVTemplate
          open={openImportTemplate}
          handleClose={() => setOpenImportTemplate(false)}
          addCSVTemplateStructure={addTemplateStructure}
        />
        {targetFile && (
          <RemoveFileDialog
            open={openRemoveFileDialog}
            file={targetFile}
            handleClose={handleDialogClose}
            handleSubmit={handleRemoveFile}
          />
        )}
        <ToastContainer closeOnClick={false} closeButton={false} />
      </div>
    </div>
  );
};

export default PersonalTemplates;
